'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Eye } from 'lucide-react';
import { isViewOnly } from '@/utils/auth';

/**
 * Sticky banner shown to a read-only (inactive-account) session. Renders
 * nothing for full-access users. The server enforces the restriction; this
 * just tells the user why actions are unavailable.
 *
 * The account's status is re-checked server-side on every request: if it
 * changes, the session is revoked (401 SESSION_REVOKED) and the user is
 * returned to the login form, so this banner can never be left showing a
 * state that has since been corrected.
 */
export default function ViewOnlyBanner() {
  const pathname = usePathname();
  const [viewOnly, setViewOnly] = useState(false);

  useEffect(() => {
    setViewOnly(isViewOnly());
  }, [pathname]);

  if (!viewOnly) return null;

  return (
    <div
      data-testid="view-only-banner"
      className="sticky top-0 z-20 flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-900 text-xs sm:text-sm"
    >
      <Eye size={15} className="flex-shrink-0" />
      <span>
        <strong className="font-semibold">View only.</strong>{' '}
        Your account is inactive, you can browse everything but can’t make
        changes. Contact your school administrator to reactivate.
      </span>
    </div>
  );
}
